import {
  EquityFieldKey,
  LegacyEquity,
  ScannerID,
  StaticScanner,
  AnyEquity,
} from 'types';
import { getDaysUntil } from 'util/shared/date';
import { CROSS_ASSET_SUMMARY_SYMS, SECTOR_ETFS } from './equityhub';
import { sortByWallMove, passesWallMoveFilter } from 'util/equityhub';
import { minVolumeThresholdFilter } from 'util/shared';

export const EXCLUDED_SQUEEZE_SYMS = new Set([
  'ARKK',
  'GDX',
  'GDXJ',
  'IBB',
  'ITB',
  'IWM',
  'IYE',
  'IYR',
  'KBE',
  'KRE',
  'KWEB',
  'MAGS',
  'MGK',
  'OIH',
  'QQQ',
  'SMH',
  'SPY',
  'SQQQ',
  'TQQQ',
  'VNQ',
  'XAR',
  'XBI',
  'XHB',
  'XLB',
  'XLC',
  'XLE',
  'XLI',
  'XLK',
  'XLP',
  'XLRE',
  'XLU',
  'XLV',
  'XME',
  'XOP',
  'XRT',
]);

export const SCANNERS: Map<ScannerID, StaticScanner> = new Map(
  [
    {
      id: ScannerID.CLOSE_TO_HEDGE_WALL,
      passes: (e: AnyEquity) =>
        'pws' in e &&
        (e.upx - e.pws) / e.upx <= 0 &&
        (e.upx - e.pws) / e.upx >= -0.01,
    },
    {
      id: ScannerID.CLOSE_TO_KEY_DELTA_STRIKE,
      passes: (e: AnyEquity) =>
        'keyd' in e &&
        (e.upx - e.keyd) / e.upx <= 0 &&
        (e.upx - e.keyd) / e.upx >= -0.01,
    },
    {
      id: ScannerID.TOP_GAMMA_EXP,
      passes: (e: AnyEquity) =>
        e.next_exp_g > 0.25 && getDaysUntil(e.max_exp_g_date) < 7,
      comparator: (a: AnyEquity, b: AnyEquity) => b.next_exp_g - a.next_exp_g,
    },
    {
      id: ScannerID.TOP_DELTA_EXP,
      passes: (e: AnyEquity) =>
        e.next_exp_d > 0.25 && getDaysUntil(e.max_exp_d_date) < 7,
      comparator: (a: AnyEquity, b: AnyEquity) => b.next_exp_d - a.next_exp_d,
    },
    {
      id: ScannerID.SQUEEZE,
      passes: (e: AnyEquity) =>
        (e.squeeze_scanner ?? 0) > 0 && !EXCLUDED_SQUEEZE_SYMS.has(e.sym),
    },
    {
      id: ScannerID.VOL_RISK_PREMIUM,
      passes: (e: AnyEquity) => (e.vrp_scanner_high ?? 0) > 0,
    },
    {
      id: ScannerID.REVERSE_VOL_RISK_PREMIUM,
      passes: (e: AnyEquity) => (e.vrp_scanner ?? 0) > 0,
    },
    {
      id: ScannerID.CROSS_ASSET_SUMMARY,
      passes: (e: AnyEquity) => CROSS_ASSET_SUMMARY_SYMS.includes(e.sym),
      comparator: (a: AnyEquity, b: AnyEquity) =>
        CROSS_ASSET_SUMMARY_SYMS.indexOf(a.sym) -
        CROSS_ASSET_SUMMARY_SYMS.indexOf(b.sym),
    },
    {
      id: ScannerID.EARNINGS_IV_CRUSH,
      passes: (e: AnyEquity) =>
        e.stock_volume_30d_avg > 1_500_000 &&
        e.atm_iv30 / e.rv30 >= 1.25 &&
        e.iv_slope_ne_45 <= -0.004_06 &&
        e.earnings_utc != null,
      comparator: (a: AnyEquity, b: AnyEquity) => b.iv_rank - a.iv_rank,
    },
    {
      id: ScannerID.HIGHEST_OPTIONS_IMPACT,
      passes: (e: AnyEquity) => {
        const impact = e[EquityFieldKey.optionsImpact];
        return impact != null && impact > 0;
      },
      comparator: (a: AnyEquity, b: AnyEquity) =>
        b[EquityFieldKey.optionsImpact]! - a[EquityFieldKey.optionsImpact]!,
      slice: 50,
    },
    {
      id: ScannerID.CALL_WALL_INCREASE,
      passes: (e: AnyEquity) =>
        passesWallMoveFilter(
          e as LegacyEquity,
          EquityFieldKey.callWall,
          EquityFieldKey.prevCallWall,
          'increase',
        ),
      comparator: (a: AnyEquity, b: AnyEquity) =>
        sortByWallMove(
          a as LegacyEquity,
          b as LegacyEquity,
          EquityFieldKey.callWall,
          EquityFieldKey.prevCallWall,
        ),
      slice: 25,
    },
    {
      id: ScannerID.CALL_WALL_DECREASE,
      passes: (e: AnyEquity) =>
        passesWallMoveFilter(
          e as LegacyEquity,
          EquityFieldKey.callWall,
          EquityFieldKey.prevCallWall,
          'decrease',
        ),
      comparator: (a: AnyEquity, b: AnyEquity) =>
        sortByWallMove(
          a as LegacyEquity,
          b as LegacyEquity,
          EquityFieldKey.callWall,
          EquityFieldKey.prevCallWall,
        ),
      slice: 25,
    },
    {
      id: ScannerID.PUT_WALL_INCREASE,
      passes: (e: AnyEquity) =>
        passesWallMoveFilter(
          e as LegacyEquity,
          EquityFieldKey.putWall,
          EquityFieldKey.prevPutWall,
          'increase',
        ),
      comparator: (a: AnyEquity, b: AnyEquity) =>
        sortByWallMove(
          a as LegacyEquity,
          b as LegacyEquity,
          EquityFieldKey.putWall,
          EquityFieldKey.prevPutWall,
        ),
      slice: 25,
    },
    {
      id: ScannerID.PUT_WALL_DECREASE,
      passes: (e: AnyEquity) =>
        passesWallMoveFilter(
          e as LegacyEquity,
          EquityFieldKey.putWall,
          EquityFieldKey.prevPutWall,
          'decrease',
        ),
      comparator: (a: AnyEquity, b: AnyEquity) =>
        sortByWallMove(
          a as LegacyEquity,
          b as LegacyEquity,
          EquityFieldKey.putWall,
          EquityFieldKey.prevPutWall,
        ),
      slice: 25,
    },
    {
      id: ScannerID.HEDGE_WALL_INCREASE,
      passes: (e: AnyEquity) =>
        passesWallMoveFilter(
          e as LegacyEquity,
          EquityFieldKey.hedgeWall,
          EquityFieldKey.prevHedgeWall,
          'increase',
        ),
      comparator: (a: AnyEquity, b: AnyEquity) =>
        sortByWallMove(
          a as LegacyEquity,
          b as LegacyEquity,
          EquityFieldKey.hedgeWall,
          EquityFieldKey.prevHedgeWall,
        ),
      slice: 25,
    },
    {
      id: ScannerID.HEDGE_WALL_DECREASE,
      passes: (e: AnyEquity) =>
        passesWallMoveFilter(
          e as LegacyEquity,
          EquityFieldKey.hedgeWall,
          EquityFieldKey.prevHedgeWall,
          'decrease',
        ),
      comparator: (a: AnyEquity, b: AnyEquity) =>
        sortByWallMove(
          a as LegacyEquity,
          b as LegacyEquity,
          EquityFieldKey.hedgeWall,
          EquityFieldKey.prevHedgeWall,
        ),
      slice: 25,
    },
    {
      id: ScannerID.IV_PERCENT_CHANGE,
      passes: (_: AnyEquity) => true,
      comparator: (a: AnyEquity, b: AnyEquity) =>
        b.atm_iv30_pct_chg - a.atm_iv30_pct_chg,
      slice: 50,
    },
    {
      id: ScannerID.SECTOR_ETFS,
      passes: (e: AnyEquity) => SECTOR_ETFS.includes(e.sym),
    },
  ].map((s) => {
    // Apply minimum volume threshold filter first for all scanners except CROSS_ASSET_SUMMARY and SECTOR_ETFS
    if (
      ![ScannerID.CROSS_ASSET_SUMMARY, ScannerID.SECTOR_ETFS].includes(s.id)
    ) {
      const passes = s.passes;
      s.passes = (e) => minVolumeThresholdFilter(e) && passes(e);
    }
    return [s.id, s];
  }),
);
